import { onBeforeUnmount, onMounted } from "vue";
import { usePwaStore } from "../../store/pwa";
import { SERVICE_WORKER_URL, UPDATE_CHECK_INTERVAL_MS } from "../constants/pwa";
import {
  forgetInstallPrompt,
  isRunningInstalled,
  keepInstallPrompt,
  type BeforeInstallPromptEvent,
} from "../helpers/pwa/installPrompt";

/**
 * Подключение кабинета как устанавливаемого приложения (CAB-489).
 *
 * Делает три вещи: регистрирует service worker, замечает готовую новую версию
 * и ловит предложение установки. Всё — один раз на приложение, из `MainLayout`.
 *
 * Воркер один на origin, и его же адрес отдаётся виджету Сигнала для подписки
 * на push: вторая регистрация на области `/` заместила бы первую вместе с
 * подпиской, и уведомления перестали бы приходить (см. `public/sw.js`).
 */
export function usePwa(): void {
  const store = usePwaStore();

  let registration: ServiceWorkerRegistration | null = null;
  let updateTimer = 0;

  function isSupported(): boolean {
    return typeof navigator !== "undefined" && "serviceWorker" in navigator && window.isSecureContext !== false;
  }

  /**
   * Новая версия считается готовой, только когда рядом с уже работающим
   * воркером встал новый. Первая в жизни установка — не обновление: предлагать
   * «обновиться» человеку, который только что открыл кабинет, бессмысленно.
   */
  function watchInstalling(worker: ServiceWorker | null): void {
    if (!worker) return;

    worker.addEventListener("statechange", () => {
      if (worker.state === "installed" && navigator.serviceWorker.controller) {
        store.isUpdateReady = true;
      }
    });
  }

  async function register(): Promise<void> {
    try {
      registration = await navigator.serviceWorker.register(SERVICE_WORKER_URL);

      if (registration.waiting && navigator.serviceWorker.controller) {
        store.isUpdateReady = true;
      }

      watchInstalling(registration.installing);
      registration.addEventListener("updatefound", () => watchInstalling(registration?.installing ?? null));

      // Долгий сеанс без перезагрузки — обычное дело: кабинет держат открытым
      // весь рабочий день. Без периодической проверки о новой версии узнали бы
      // только назавтра.
      updateTimer = window.setInterval(() => {
        registration?.update();
      }, UPDATE_CHECK_INTERVAL_MS);
    } catch (error) {
      console.error("PWA: не удалось зарегистрировать service worker", error);
    }
  }

  function onBeforeInstallPrompt(event: Event): void {
    // Браузер сам показал бы своё предложение не вовремя — забираем момент себе
    event.preventDefault();
    keepInstallPrompt(event as BeforeInstallPromptEvent);
    store.canInstall = true;
  }

  function onInstalled(): void {
    forgetInstallPrompt();
    store.canInstall = false;
    store.isInstalled = true;
  }

  onMounted(() => {
    store.isInstalled = isRunningInstalled();

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onInstalled);

    if (isSupported()) void register();
  });

  onBeforeUnmount(() => {
    window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.removeEventListener("appinstalled", onInstalled);
    window.clearInterval(updateTimer);
  });
}

/**
 * Переключает кабинет на скачанную версию. Ждущий воркер берёт управление и
 * страница перезагружается один раз — повторные `controllerchange` при
 * последующих обновлениях уже не должны её дёргать.
 */
export async function applyPwaUpdate(): Promise<void> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;

  const registration = await navigator.serviceWorker.getRegistration(SERVICE_WORKER_URL);
  const waiting = registration?.waiting;
  if (!waiting) {
    window.location.reload();
    return;
  }

  let reloaded = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (reloaded) return;
    reloaded = true;
    window.location.reload();
  });

  waiting.postMessage({ type: "SKIP_WAITING" });
}
